/**
 * How a team might run its board, and the instruction that tells an agent
 * (TOADO-972). One source for the board's help and, later, the MCP setup tour
 * (TOADO-974), so the two never give an agent different rules.
 *
 * Everything here is a recommendation or an example, never the board's rule:
 * groups use In Review and Done differently, and only the team can say what a
 * stage means for it. The only meanings the system itself holds are the flags
 * on a column (Bug intake, waiting on the reporter); nothing here is inferred
 * from a column's name or place.
 */

/** A stage of the recommended starting workflow. */
export interface RecommendedStage {
  name: string;
  meaning: string;
}

export const RECOMMENDED_WORKFLOW: readonly RecommendedStage[] = [
  { name: 'To Do', meaning: 'Work ready to pick up.' },
  { name: 'In Progress', meaning: 'A person or an agent has picked it up.' },
  { name: 'Needs reply', meaning: 'The reporter needs to answer a question or make a decision before work can go on.' },
  { name: 'In Review', meaning: 'The agreed reviewer checks the work, under the conditions the team chose, at a review location the team named.' },
  { name: 'Done', meaning: 'The team\'s checks, acceptance and delivery requirements are met.' },
];

/** The answers a workflow needs, the same for the starter workflow and every example. */
export interface WorkflowAnswers {
  /** What has to be true for a ticket to move into review. */
  entersReview: string;
  /** Who checks it. */
  reviewer: string;
  /** Where it is checked: a pull request, a preview or staging URL, the live site. */
  reviewLocation: string;
  /** What the agent leaves on the ticket for the reviewer. */
  evidence: string;
  /** What has to be true for Done. */
  doneWhen: string;
  /** Whether and how the agent may deploy, and who authorises it. */
  deployment: string;
}

export interface WorkflowExample {
  key: 'code-review' | 'preview-qa' | 'live-acceptance' | 'custom';
  title: string;
  summary: string;
  /** Suggested answers, all editable; the custom example suggests none. */
  answers: Partial<WorkflowAnswers>;
}

export const WORKFLOW_EXAMPLES: readonly WorkflowExample[] = [
  {
    key: 'code-review',
    title: 'Code review before merge',
    summary: 'In Review means a pull request is open and waiting for a reviewer. Done means it is merged.',
    answers: {
      entersReview: 'A pull request is open, its checks pass, and it is linked on the ticket.',
      reviewer: 'A developer on the team who did not write the change.',
      reviewLocation: 'The pull request.',
      evidence: 'The pull request link, what changed and why, and the checks that ran.',
      doneWhen: 'The pull request is approved and merged.',
      deployment: 'The agent does not deploy. Merging is the reviewer\'s call.',
    },
  },
  {
    key: 'preview-qa',
    title: 'Preview or staging QA',
    summary: 'In Review means the change is on a preview or staging site for someone to try. Done means it passed there.',
    answers: {
      entersReview: 'The change is deployed to a preview or staging site.',
      reviewer: 'The person who reported it, or the team\'s QA person.',
      reviewLocation: 'The preview or staging URL, linked on the ticket.',
      evidence: 'The preview link, the steps to check it, and what changed.',
      doneWhen: 'The reviewer confirms it works on the preview or staging site.',
      deployment: 'The agent may deploy to preview or staging only. Production needs the team\'s go-ahead.',
    },
  },
  {
    key: 'live-acceptance',
    title: 'Live acceptance',
    summary: 'In Review means the change is live and the reporter checks it there. Done means they accepted it.',
    answers: {
      entersReview: 'The change is live.',
      reviewer: 'The person who reported it.',
      reviewLocation: 'The live site, at the page the ticket was reported on.',
      evidence: 'When it went live, what changed, and how to check it.',
      doneWhen: 'The reporter confirms it is fixed on the live site.',
      deployment: 'Deploys follow the team\'s usual release process; the agent does not deploy on its own.',
    },
  },
  {
    key: 'custom',
    title: 'Your own process',
    summary: 'Write down what In Review and Done mean for your team.',
    answers: {},
  },
];

/** A column of the project as the instruction names it. */
export interface InstructionColumn {
  id: string;
  name: string;
  /** Anonymous widget reports land here while Bug intake is on. */
  isIntake?: boolean;
  /** Its tickets wait on whoever reported them. */
  waitingOnReporter?: boolean;
  /** Where tickets finish: the board's done column. */
  isTerminal?: boolean;
}

export interface InstructionDraft {
  projectName: string;
  columns: readonly InstructionColumn[];
  /** Whether the project's Bug intake is on, so its column takes outside reports. */
  intakeOn?: boolean;
  /** The column the team uses for review; see `suggestedColumns`. */
  reviewColumnId?: string | null;
  /** The column for finished work; see `suggestedColumns`. */
  doneColumnId?: string | null;
  answers: Partial<WorkflowAnswers>;
}

/**
 * The review and done columns an example fills in (Jason, 30 Oct, t-Zw5xUW), so it
 * reads as an answer rather than "[not decided yet]", from the board's flags and
 * order, never a column's name. Done is the column marked as where tickets finish.
 * Review is the last ordinary column before it (not intake, not waiting on the
 * reporter), on a board with at least three ordinary columns, so To Do, In Progress,
 * Done does not have In Progress named as its review. Either stays undecided when
 * the board does not say; the text is the team's to change.
 */
export function suggestedColumns(columns: readonly InstructionColumn[]): { reviewColumnId: string | null; doneColumnId: string | null } {
  const done = columns.find((column) => column.isTerminal);
  const ordinary = columns.filter((column) => !column.isTerminal && !column.isIntake && !column.waitingOnReporter);
  const review = done && ordinary.length >= 3 ? ordinary[ordinary.length - 1] : undefined;
  return { reviewColumnId: review?.id ?? null, doneColumnId: done?.id ?? null };
}

/** What an unanswered choice reads as, so it stays visibly open in the instruction. */
export const NOT_DECIDED = '[not decided yet]';

/** The first of the closing lines every example ends with; column notes go above them. */
export const INSTRUCTIONS_CLOSING_LINE = 'Moving a ticket on the board does not deploy anything or prove that checks passed; say on the ticket what you did and what you checked.';

/** How the instructions say what happens in a column, one line each (Jason, 30 Oct, t-kF6Der). */
export function columnNotePrefix(columnName: string): string {
  return `When a ticket is in "${columnName}":`;
}

/** What the instructions say happens in a column, or null when they say nothing. */
export function readColumnNote(text: string, columnName: string): string | null {
  const prefix = columnNotePrefix(columnName);
  const line = text.split('\n').find((candidate) => candidate.startsWith(prefix));
  return line === undefined ? null : line.slice(prefix.length).trim();
}

/**
 * The instructions with a line for a column, and where the caret goes to finish
 * it: the end of the column's line, which is added if there is none. A new line
 * goes with any other column lines above the closing lines, or at the end.
 */
export function withColumnNote(text: string, columnName: string): { text: string; caret: number } {
  const prefix = columnNotePrefix(columnName);
  const lines = text.split('\n');
  const end = (upTo: readonly string[], index: number) => upTo.slice(0, index + 1).join('\n').length;
  const existing = lines.findIndex((line) => line.startsWith(prefix));
  if (existing >= 0) return { text, caret: end(lines, existing) };
  const note = `${prefix} `;
  const closing = lines.indexOf(INSTRUCTIONS_CLOSING_LINE);
  if (closing < 0) {
    const body = text.replace(/\s+$/, '');
    const next = `${body ? `${body}\n\n` : ''}${note}`;
    return { text: next, caret: next.length };
  }
  // Under the column lines already there, kept as one group; else a group of its own.
  const afterNotes = closing >= 2 && lines[closing - 1] === '' && lines[closing - 2]!.startsWith('When a ticket is in "');
  const at = afterNotes ? closing - 1 : closing;
  const next = [...lines.slice(0, at), note, ...(afterNotes ? [] : ['']), ...lines.slice(at)];
  return { text: next.join('\n'), caret: end(next, at) };
}

const answer = (value: string | undefined) => (value && value.trim() ? value.trim() : NOT_DECIDED);

/**
 * A starting text for the project's instructions for agents (TOADO-998), which the
 * team edits and saves, and get_ticket_brief hands to every agent. Plain text, with
 * the project's real names; anything not decided says so rather than being filled in
 * with a default. It names only the columns its rules need: an agent reads the board
 * itself (list_columns), and a saved list of every column would go stale as the board
 * changed. Nothing in it asks the agent to reply, since every agent reads it on every
 * ticket rather than once in a conversation.
 */
export function buildAgentInstructions(draft: InstructionDraft): string {
  const named = (id: string | null | undefined) => draft.columns.find((column) => column.id === id)?.name;
  const review = named(draft.reviewColumnId);
  const done = named(draft.doneColumnId);
  const waiting = draft.columns.filter((column) => column.waitingOnReporter).map((column) => column.name);
  const intake = draft.intakeOn ? draft.columns.filter((column) => column.isIntake).map((column) => column.name) : [];
  const quote = (name: string) => `"${name}"`;
  const lines = [
    `How we work on the Toado board for ${draft.projectName}`,
    '',
  ];
  if (waiting.length) {
    lines.push(`When you need the reporter to answer a question or decide something before you can go on, move the ticket to ${waiting.map(quote).join(' or ')} and ask on the ticket. Do not carry on until they answer.`);
  }
  if (intake.length) {
    lines.push(`${intake.map(quote).join(', ')} holds new reports from outside the team for triage. Do not start work on a ticket there until someone has moved it out.`);
  }
  lines.push(
    '',
    `Review column: ${review ? quote(review) : NOT_DECIDED}`,
    `A ticket moves to review when: ${answer(draft.answers.entersReview)}`,
    `Who reviews it: ${answer(draft.answers.reviewer)}`,
    `Where it is reviewed: ${answer(draft.answers.reviewLocation)}`,
    `What to leave on the ticket for the reviewer: ${answer(draft.answers.evidence)}`,
    '',
    `Done column: ${done ? quote(done) : NOT_DECIDED}`,
    `A ticket is done when: ${answer(draft.answers.doneWhen)}`,
    `Deploying: ${answer(draft.answers.deployment)}`,
    '',
    INSTRUCTIONS_CLOSING_LINE,
  );
  // Only while something is undecided: a fully answered example has nothing to ask about.
  if (lines.some((line) => line.includes(NOT_DECIDED))) lines.push(`Where something above says ${NOT_DECIDED}, ask before you act on it.`);
  return lines.join('\n');
}
